import { getPurchaseRequestPage } from "@/apis/requirementManagement.api";
import BaseProtable from "@/components/BaseProTable";
import { PrimaryButton } from "@/components/ui/buttons/PrimaryButton";
import CommonModal from "@/components/ui/CommonModal";
import { PurchaseRequestTypeOptions } from "@/constants";
import { usePageTable } from "@/hooks/baseProTable/usePageTable";
import type {
  SrmPurchaseRequestListQueryDto,
  SrmPurchaseRequestVo,
} from "@/types/apis";
import { notifyWarning } from "@/utils/notification";
import { type TableProps } from "antd";
import { useState } from "react";

type ListItem = SrmPurchaseRequestVo;
type SearchParams = SrmPurchaseRequestListQueryDto;

type Props = {
  open: boolean;
  onOk: (checkedRows: ListItem[]) => void;
  onClose: () => void;
  /** 选择模式，默认多选 */
  selectionMode?: "multiple" | "single";
};

export const PurchaseRequestPageSelectorModal: React.FC<Props> = ({
  open,
  onOk,
  onClose,
  selectionMode = "multiple",
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<ListItem[]>([]);
  const [params, setParams] = useState<SearchParams>({});

  const { actionRef, fetchDatasource, columns, formRef } = usePageTable<
    ListItem,
    SearchParams
  >({
    fetchFn: getPurchaseRequestPage,
    columns: [
      {
        title: "序号",
        valueType: "index",
        width: 60,
        align: "center",
        fixed: "left",
      },
      {
        title: "采购申请单编号",
        dataIndex: "applicationNo",
        width: 150,
        align: "center",
      },
      {
        title: "物料",
        dataIndex: "materialInfo",
        key: "materialInfo",
        hideInTable: true,
        fieldProps: {
          placeholder: "请输入物料编号/物料名称",
        },
        order: 99,
      },
      {
        title: "申请表类型",
        dataIndex: "formType",
        key: "formType",
        hideInTable: true,
        order: 97,
        valueType: "select",
        fieldProps: {
          options: PurchaseRequestTypeOptions,
        },
      },
      {
        title: "工厂",
        dataIndex: "factoryName",
        key: "factory",
        align: "center",
        search: false,
      },
      {
        title: "物料类别",
        dataIndex: "materialType1Name",
        align: "center",
        search: false,
      },
    ],
  });

  const rowSelection: TableProps<ListItem>["rowSelection"] = {
    type: selectionMode === "single" ? "radio" : "checkbox",
    selectedRowKeys,
    onChange: (selectedRowKeys, selectedRows) => {
      if (selectionMode === "single") {
        setSelectedRowKeys(selectedRowKeys.slice(0, 1));
        setSelectedRows(selectedRows.slice(0, 1));
        return;
      }
      setSelectedRowKeys(selectedRowKeys);
      setSelectedRows(selectedRows);
    },
  };

  const handleOk = () => {
    if (selectedRows.length === 0) {
      notifyWarning("请选择采购申请单");
      return;
    }
    onOk?.(selectedRows);
    handleClose();
  };

  const handleClose = () => {
    setSelectedRowKeys([]);
    setSelectedRows([]);
    setParams({});
    onClose();
  };

  const footer = () => (
    <div style={{ textAlign: "center" }}>
      <PrimaryButton onClick={handleOk}>确定</PrimaryButton>
    </div>
  );

  return (
    <CommonModal
      title="选择采购申请表"
      open={open}
      onCancel={handleClose}
      width={1200}
      style={{ top: 20 }}
      footer={footer}
    >
      <BaseProtable<ListItem, SearchParams>
        headerTitle={null}
        actionRef={actionRef}
        formRef={formRef}
        columns={columns}
        rowSelection={rowSelection}
        bordered
        request={fetchDatasource}
        params={params}
        search={{
          labelWidth: "auto",
          span: 6,
        }}
        onSubmit={(values) => setParams({ ...values })}
      />
    </CommonModal>
  );
};
